import { z } from "zod"
import { apiListSchema } from "./list"

export enum TriggerEntityType {
  Accommodation = "Accommodation",
  Service = "ReservationService",
}

// export enum TriggerEventType {
//   Created = 1,
//   Updated = 2,
//   StateChanged = 3,
//   Custom = 4,
// }

// Status enums for different entity types
export enum AccommodationState {
   No = "No",
   SentToHotel = "SentToHotel",
   Ongoing = "Ongoing",
   Phone = "Phone",
   PreBooking = "PreBooking",
   Ok = "Ok",
   OkPromo = "OkPromo",
   SelfBooked = "SelfBooked",
   BookingCom = "BookingCom",
   Canceled = "Canceled",
}

export enum ServiceState {
  No = "No",
  SentToProvider = "SentToProvider",
  Ongoing = "Ongoing",
  Phone = "Phone",
  PreBooking = "PreBooking",
  Ok = "Ok",
  Done = "Done",
  PercentDone = "PercentDone",
  NotDone = "NotDone",
  Canceled = "Canceled",
}

// DTO Schemas
export const CreateTriggerDtoSchema = z.object({
  Name: z.string().min(1),
  Description: z.string().nullish(),
  EntityType: z.nativeEnum(TriggerEntityType),
  Condition: z.string().nullish(), // JSON string with condition details
  EmailTemplateId: z.number(),
  Active: z.boolean(),
  Recipients: z.string().nullish(), // JSON string with recipient details
  CallbackUrl: z.string().nullish(),
})

export type CreateTriggerDto = z.infer<typeof CreateTriggerDtoSchema>

export const TriggerDtoSchema = CreateTriggerDtoSchema.extend({
  Id: z.number(),
  CreatedAt: z.string(),
  UpdatedAt: z.string(),
})

export type TriggerDto = z.infer<typeof TriggerDtoSchema>
export const triggerDtoListSchema = apiListSchema(TriggerDtoSchema)

// Model Schemas
export const ConditionSchema = z.object({
  id: z.string(),
  field: z.string(),
  currentValue: z.string().nullish(),
  beforeValue: z.string().nullish()

})

export type Condition = z.infer<typeof ConditionSchema>

export const RecipientSchema = z.object({
  type: z.enum(["static", "dynamic", "entity", "worker"]),
  value: z.string(), // Email for static, field path for dynamic/entity, worker ID for worker
})

export type Recipient = z.infer<typeof RecipientSchema>

export const CreateTriggerSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullish(),
  entityType: z.nativeEnum(TriggerEntityType),
  conditions: z.array(ConditionSchema).nullish(),
  emailTemplateId: z.string(),
  active: z.boolean().default(true),
  recipients: z.array(RecipientSchema).nullish(),
  callbackUrl: z.string().nullish(),
})

export type CreateTrigger = z.infer<typeof CreateTriggerSchema>

export interface Trigger {
  id: string
  name: string
  description?: string
  entityType: TriggerEntityType
  // eventType: TriggerEventType
  conditions: Condition[]
  emailTemplateId: string
  active: boolean
  recipients: Recipient[]
  callbackUrl?: string
  createdAt: string
  updatedAt: string
}

// Mappers
export function createTriggerToDto(trigger: CreateTrigger): CreateTriggerDto {
  return {
    Name: trigger.name,
    Description: trigger.description,
    EntityType: trigger.entityType,
    Condition: trigger.conditions ? JSON.stringify(trigger.conditions) : null,
    EmailTemplateId: Number.parseInt(trigger.emailTemplateId),
    Active: trigger.active,
    Recipients: trigger.recipients ? JSON.stringify(trigger.recipients) : null,
    CallbackUrl: trigger.callbackUrl,
  }
}

export function triggerFromDto(dto: TriggerDto): Trigger {
  return {
    id: dto.Id.toString(),
    name: dto.Name,
    description: dto.Description || undefined,
    entityType: dto.EntityType,
    conditions: dto.Condition ? JSON.parse(dto.Condition) : [],
    emailTemplateId: dto.EmailTemplateId.toString(),
    active: dto.Active,
    recipients: dto.Recipients ? JSON.parse(dto.Recipients) : [],
    callbackUrl: dto.CallbackUrl || undefined,
    createdAt: dto.CreatedAt,
    updatedAt: dto.UpdatedAt,
  }
}

export function triggerToDto(trigger: Trigger): TriggerDto {
  return {
    Id: Number.parseInt(trigger.id),
    Name: trigger.name,
    Description: trigger.description,
    EntityType: trigger.entityType,
    // EventType: trigger.eventType,
    Condition: trigger.conditions.length > 0 ? JSON.stringify(trigger.conditions) : null,
    EmailTemplateId: Number.parseInt(trigger.emailTemplateId),
    Active: trigger.active,
    Recipients: trigger.recipients.length > 0 ? JSON.stringify(trigger.recipients) : null,
    CallbackUrl: trigger.callbackUrl,
    CreatedAt: trigger.createdAt,
    UpdatedAt: trigger.updatedAt,
  }
}

// Constants for UI
export const ENTITY_TYPES = [
  { value: TriggerEntityType.Accommodation, label: "Accommodation" },
  { value: TriggerEntityType.Service, label: "Service" },
]

// Fields available for each entity type for conditions
export const ENTITY_FIELDS = {
  [TriggerEntityType.Accommodation]: [{ name: "Accommodation.State", label: "State" }],
  [TriggerEntityType.Service]: [{ name: "ReservationService.State", label: "State" }],
}

// Field value options for different fields with integer values
export const FIELD_VALUE_OPTIONS = {
  "Accommodation.State": [
    { value: AccommodationState.No, label: "Hotel Declined" },
    { value: AccommodationState.SentToHotel, label: "Sent To Hotel" },
    { value: AccommodationState.Ongoing, label: "Ongoing Reservation" },
    { value: AccommodationState.Phone, label: "Requires Contact" },
    { value: AccommodationState.PreBooking, label: "Pre Booking" },
    { value: AccommodationState.Ok, label: "Hotel Confirmed" },
    { value: AccommodationState.OkPromo, label: "Ok Promo" },
    // { value: AccommodationState.SelfBooked, label: "Self-Booked" },
    // { value: AccommodationState.BookingCom, label: "Booking.com" },
    { value: AccommodationState.Canceled, label: "Canceled" },
  ],
  "ReservationService.State": [
    { value: ServiceState.No, label: "Provider Declined" },
    { value: ServiceState.SentToProvider, label: "Sent To Provider" },
    { value: ServiceState.Ongoing, label: "Ongoing Service" },
    { value: ServiceState.Phone, label: "Requires Contact" },
    { value: ServiceState.PreBooking, label: "Pre Booking" },
    { value: ServiceState.Ok, label: "Provider Confirmed" },
    { value: ServiceState.Done, label: "Done" },
    { value: ServiceState.PercentDone, label: "Somewhat Done" },
    { value: ServiceState.NotDone, label: "Not Done" },
    { value: ServiceState.Canceled, label: "Canceled" },
  ],
}

export const CONDITION_OPERATORS = [{ value: "equals", label: "Equals" }]

// Recipient types - simplified to just entity field and static
export const RECIPIENT_TYPES = [
  { value: "entity", label: "Contact Field" },
  { value: "static", label: "Static Email" },
]

// Recipient field options
export const RECIPIENT_FIELD_OPTIONS = [
  { name: "Accomodation.HotelRoom.Hotel.Email", label: "Hotel Email" },
  { name: "ReservationService.ServiceProvider.Person.Email", label: "Provider Email" },
  { name: "ReservationService.Worker.Person.Email", label: "Reservation Worker Email" },
]
